import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Container,
  Flex,
  Heading,
  Section,
  Table,
  Text,
} from '@/components/ui';
import {
  Avatar,
  type AvatarColor,
  type AvatarSize,
  type AvatarVariant,
} from '@/components/ui/Avatar';
import { Tooltip } from '@radix-ui/themes';

const variants: AvatarVariant[] = [
  'contained',
  'outlined',
  'text',
  'classic',
  'soft',
  'surface',
  'ghost',
  'dashed',
];

const colors: AvatarColor[] = [
  'default',
  'primary',
  'secondary',
  'success',
  'warning',
  'error',
  'info',
  'destructive',
];

const sizes: AvatarSize[] = [
  'xsmall',
  'small',
  'medium',
  'large',
  'xlarge',
  'xxlarge',
];

const statuses: Array<'online' | 'offline' | 'away'> = [
  'online',
  'offline',
  'away',
];

export const AvatarDemoPage: React.FC = () => {
  return (
    <Container pt="4" pb="4" size="expand">
      <Section>
        <Flex direction="column" gap="6">
          <div>
            <Heading size="large" className="mb-2">
              Avatar Component Demo
            </Heading>
            <Text size="2" color="gray">
              Avatar component with multiple variants, colors, and sizes.
              Supports image sources, fallback text, and status indicators.
            </Text>
          </div>

          {/* Variants and Colors Table */}
          <Card>
            <CardHeader>
              <CardTitle>Variants and Colors</CardTitle>
            </CardHeader>
            <CardContent>
              <Flex direction="column" gap="4">
                <Text size="2" color="gray">
                  All variants and colors in a table format. Rows represent
                  variants, columns represent colors.
                </Text>
                <div className="overflow-x-auto">
                  <Table.Root
                    columns={`150px repeat(${colors.length}, 1fr)`}
                    noScrollArea
                  >
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell>Variant</Table.HeaderCell>
                        {colors.map(color => (
                          <Table.HeaderCell
                            key={color}
                            style={{ textAlign: 'center' }}
                          >
                            {color}
                          </Table.HeaderCell>
                        ))}
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {variants.map(variant => (
                        <Table.Row key={variant}>
                          <Table.Cell>
                            <Text size="2" weight="medium">
                              {variant}
                            </Text>
                          </Table.Cell>
                          {colors.map(color => (
                            <Table.Cell
                              key={`${variant}-${color}`}
                              style={{ textAlign: 'center' }}
                            >
                              <Tooltip
                                content={`Variant: ${variant}, Color: ${color}`}
                              >
                                <div className="flex justify-center">
                                  <Avatar
                                    variant={variant}
                                    color={color}
                                    fallback="AB"
                                  />
                                </div>
                              </Tooltip>
                            </Table.Cell>
                          ))}
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table.Root>
                </div>
              </Flex>
            </CardContent>
          </Card>

          {/* Sizes Table */}
          <Card>
            <CardHeader>
              <CardTitle>Sizes</CardTitle>
            </CardHeader>
            <CardContent>
              <Flex direction="column" gap="4">
                <Text size="2" color="gray">
                  All available sizes of the Avatar component with different
                  variants.
                </Text>
                <div className="overflow-x-auto">
                  <Table.Root columns={`120px repeat(5, 100px)`} noScrollArea>
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell>Size</Table.HeaderCell>
                        {variants.slice(0, 5).map(variant => (
                          <Table.HeaderCell
                            key={variant}
                            style={{ textAlign: 'center' }}
                          >
                            {variant}
                          </Table.HeaderCell>
                        ))}
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {sizes.map(size => (
                        <Table.Row key={size}>
                          <Table.Cell>
                            <Text size="2" weight="medium">
                              {size}
                            </Text>
                          </Table.Cell>
                          {variants.slice(0, 5).map(variant => (
                            <Table.Cell
                              key={`${size}-${variant}`}
                              style={{ textAlign: 'center' }}
                            >
                              <Tooltip
                                content={`Size: ${size}, Variant: ${variant}`}
                              >
                                <div className="flex justify-center">
                                  <Avatar
                                    size={size}
                                    variant={variant}
                                    color="primary"
                                    fallback="AB"
                                  />
                                </div>
                              </Tooltip>
                            </Table.Cell>
                          ))}
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table.Root>
                </div>
              </Flex>
            </CardContent>
          </Card>

          {/* With Image */}
          <Card>
            <CardHeader>
              <CardTitle>With Image</CardTitle>
            </CardHeader>
            <CardContent>
              <Flex direction="column" gap="4">
                <Text size="2" color="gray">
                  Avatar with image source. Falls back to initials if image
                  fails to load.
                </Text>
                <Flex gap="4" wrap="wrap" align="center">
                  <Avatar
                    src="https://i.pravatar.cc/150?img=1"
                    alt="User 1"
                    variant="contained"
                    color="primary"
                    fallback="U1"
                  />
                  <Avatar
                    src="https://i.pravatar.cc/150?img=2"
                    alt="User 2"
                    variant="soft"
                    color="success"
                    fallback="U2"
                  />
                  <Avatar
                    src="https://i.pravatar.cc/150?img=3"
                    alt="User 3"
                    variant="outlined"
                    color="warning"
                    fallback="U3"
                  />
                  <Avatar
                    src="https://i.pravatar.cc/150?img=4"
                    alt="User 4"
                    variant="classic"
                    color="primary"
                    fallback="U4"
                  />
                  <Avatar
                    src="https://i.pravatar.cc/150?img=5"
                    alt="User 5"
                    variant="dashed"
                    color="info"
                    fallback="U5"
                  />
                </Flex>
              </Flex>
            </CardContent>
          </Card>

          {/* With Status Indicators */}
          <Card>
            <CardHeader>
              <CardTitle>With Status Indicators</CardTitle>
            </CardHeader>
            <CardContent>
              <Flex direction="column" gap="4">
                <Text size="2" color="gray">
                  Avatar with status indicators (online, offline, away).
                </Text>
                <Flex gap="4" wrap="wrap" align="center">
                  {statuses.map(status => (
                    <Tooltip key={status} content={`Status: ${status}`}>
                      <div className="flex flex-col items-center gap-2">
                        <Avatar status={status} color="primary" fallback="AB" />
                        <Text size="1" color="gray" className="text-xs">
                          {status}
                        </Text>
                      </div>
                    </Tooltip>
                  ))}
                </Flex>
                <Flex gap="4" wrap="wrap" align="center">
                  {statuses.map(status => (
                    <Tooltip
                      key={`${status}-image`}
                      content={`Status: ${status}`}
                    >
                      <div className="flex flex-col items-center gap-2">
                        <Avatar
                          src={`https://i.pravatar.cc/150?img=${status === 'online' ? 1 : status === 'offline' ? 2 : 3}`}
                          alt={`User ${status}`}
                          status={status}
                          color="primary"
                          fallback="U"
                        />
                        <Text size="1" color="gray" className="text-xs">
                          {status} (image)
                        </Text>
                      </div>
                    </Tooltip>
                  ))}
                </Flex>
              </Flex>
            </CardContent>
          </Card>

          {/* Radius Variants Table */}
          <Card>
            <CardHeader>
              <CardTitle>Radius Variants</CardTitle>
            </CardHeader>
            <CardContent>
              <Flex direction="column" gap="4">
                <Text size="2" color="gray">
                  Avatar with different border radius values across variants.
                </Text>
                <div className="overflow-x-auto">
                  <Table.Root columns={`120px repeat(5, 100px)`} noScrollArea>
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell>Radius</Table.HeaderCell>
                        {variants.slice(0, 5).map(variant => (
                          <Table.HeaderCell
                            key={variant}
                            style={{ textAlign: 'center' }}
                          >
                            {variant}
                          </Table.HeaderCell>
                        ))}
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {(
                        ['none', 'small', 'medium', 'large', 'full'] as const
                      ).map(radius => (
                        <Table.Row key={radius}>
                          <Table.Cell>
                            <Text size="2" weight="medium">
                              {radius}
                            </Text>
                          </Table.Cell>
                          {variants.slice(0, 5).map(variant => (
                            <Table.Cell
                              key={`${radius}-${variant}`}
                              style={{ textAlign: 'center' }}
                            >
                              <Tooltip
                                content={`Radius: ${radius}, Variant: ${variant}`}
                              >
                                <div className="flex justify-center">
                                  <Avatar
                                    radius={radius}
                                    variant={variant}
                                    color="primary"
                                    fallback="AB"
                                  />
                                </div>
                              </Tooltip>
                            </Table.Cell>
                          ))}
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table.Root>
                </div>
              </Flex>
            </CardContent>
          </Card>

          {/* Usage Examples */}
          <Card>
            <CardHeader>
              <CardTitle>Usage Examples</CardTitle>
            </CardHeader>
            <CardContent>
              <Flex direction="column" gap="4">
                <div>
                  <Text size="2" weight="medium" className="mb-2">
                    Basic usage with fallback:
                  </Text>
                  <pre className="bg-gray-100 p-3 rounded text-sm overflow-x-auto">
                    {`<Avatar fallback="AB" color="primary" />`}
                  </pre>
                </div>
                <div>
                  <Text size="2" weight="medium" className="mb-2">
                    With image source:
                  </Text>
                  <pre className="bg-gray-100 p-3 rounded text-sm overflow-x-auto">
                    {`<Avatar 
  src="/path/to/image.jpg"
  alt="User name"
  fallback="UN"
  variant="contained"
  color="primary"
/>`}
                  </pre>
                </div>
                <div>
                  <Text size="2" weight="medium" className="mb-2">
                    With status indicator:
                  </Text>
                  <pre className="bg-gray-100 p-3 rounded text-sm overflow-x-auto">
                    {`<Avatar 
  fallback="AB"
  color="primary"
  status="online"
/>`}
                  </pre>
                </div>
                <div>
                  <Text size="2" weight="medium" className="mb-2">
                    Classic variant with gradient:
                  </Text>
                  <pre className="bg-gray-100 p-3 rounded text-sm overflow-x-auto">
                    {`<Avatar 
  variant="classic"
  color="primary"
  fallback="AB"
/>`}
                  </pre>
                </div>
                <div>
                  <Text size="2" weight="medium" className="mb-2">
                    Dashed variant:
                  </Text>
                  <pre className="bg-gray-100 p-3 rounded text-sm overflow-x-auto">
                    {`<Avatar 
  variant="dashed"
  color="info"
  fallback="AB"
/>`}
                  </pre>
                </div>
              </Flex>
            </CardContent>
          </Card>
        </Flex>
      </Section>
    </Container>
  );
};
